import React, { useState, useEffect } from 'react';
import { Drawer } from 'antd';
import { format, parseISO } from 'date-fns';
import { pl } from 'date-fns/locale';
import { fetchScheduledLeagueMatches } from '../../api/api';
import { formatFirstLetterOfName } from '../../utils';
import styles from './RecentResultsDrawer.module.css';

// Format "25 maja (wtorek)" — d MMMM (eeee) z pl locale.
function formatScheduledDate(rawDate) {
    if (!rawDate) return '';
    try {
        const date = typeof rawDate === 'string' ? parseISO(rawDate) : rawDate;
        return format(date, 'd MMMM (eeee)', { locale: pl });
    } catch {
        return rawDate;
    }
}

// Backend zwraca LocalTime jako "HH:mm:ss" — przycinamy do "HH:mm".
function formatStartTime(rawTime) {
    if (!rawTime) return '';
    return typeof rawTime === 'string' ? rawTime.substring(0, 5) : '';
}

function formatTeam(firstName, lastName, partnerFirstName, partnerLastName) {
    const main = `${lastName ?? ''} ${formatFirstLetterOfName(firstName)}`.trim();
    if (partnerFirstName || partnerLastName) {
        const partner = `${partnerLastName ?? ''} ${formatFirstLetterOfName(partnerFirstName)}`.trim();
        return `${main} / ${partner}`;
    }
    return main;
}

// Oferty turniejowe (COMBINED) dzielą tabelę z ligowymi i niosą w groupLabel albo literę
// grupy, albo sentinel "KO" dla fazy pucharowej — ten drugi nie jest nazwą grupy, więc
// dosłowne sklejenie dałoby graczowi „Grupa KO".
export function formatPhaseLabel(groupLabel) {
    if (!groupLabel) return null;
    return groupLabel === 'KO' ? 'Drabinka' : `Grupa ${groupLabel}`;
}

export function ScheduledMatchRow({ match }) {
    // slotStartTime — faktyczna godzina rezerwacji; fallback na proposedStartTime gdy slot nieustalony.
    const startTime = formatStartTime(match.slotStartTime || match.proposedStartTime);

    const metaLine1 = [
        formatPhaseLabel(match.groupLabel),
        // Oferta turniejowa ma leagueRoundNumber = 0 (w lidze numer rundy jest ≥ 1) — zero jest
        // falsy, więc „Runda" znika sama, bez osobnego warunku na typ rozgrywek.
        match.leagueRoundNumber ? `Runda ${match.leagueRoundNumber}` : null,
        formatScheduledDate(match.proposedDate),
    ].filter(Boolean).join(' · ');

    const metaLine2 = [
        startTime ? `godz. ${startTime}` : null,
        match.courtName ? match.courtName : null,
    ].filter(Boolean).join(' · ');

    const team1 = formatTeam(
        match.participant1FirstName,
        match.participant1LastName,
        match.participant1PartnerFirstName,
        match.participant1PartnerLastName,
    );
    const team2 = formatTeam(
        match.participant2FirstName,
        match.participant2LastName,
        match.participant2PartnerFirstName,
        match.participant2PartnerLastName,
    );

    return (
        <div className={styles.matchRow}>
            <div className={styles.meta}>{metaLine1}</div>
            {metaLine2 && <div className={styles.meta}>{metaLine2}</div>}
            <div className={styles.matchBody}>
                <div className={styles.player}>{team1} - {team2}</div>
            </div>
        </div>
    );
}

function ScheduledMatchesDrawer({ open, onClose, tournamentId }) {
    const [matches, setMatches] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!open || !tournamentId) return;
        let cancelled = false;
        setIsLoading(true);
        fetchScheduledLeagueMatches(tournamentId, 30)
            .then(res => {
                if (cancelled) return;
                setMatches(res.success ? res.data : []);
            })
            .catch(() => {
                if (!cancelled) setMatches([]);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => { cancelled = true; };
    }, [open, tournamentId]);

    return (
        <Drawer
            placement="bottom"
            title="Zaplanowane mecze"
            open={open}
            onClose={onClose}
            height="auto"
            headerStyle={{ maxWidth: 600, margin: '0 auto', width: '100%' }}
        >
            {isLoading && <div className={styles.loadingText}>Ładowanie...</div>}
            {!isLoading && matches.length === 0 && (
                <div className={styles.emptyText}>Brak zaplanowanych meczów</div>
            )}
            {!isLoading && matches.length > 0 && (
                <div className={styles.list}>
                    {matches.map(m => (
                        <ScheduledMatchRow key={m.proposalId} match={m} />
                    ))}
                </div>
            )}
        </Drawer>
    );
}

export default ScheduledMatchesDrawer;
